/**
 * Native inline checkout (no iframe) — the GHL-style guided order-form CARD only.
 * The desktop left value-recap column is owner LP HTML (placed beside the
 * #mdb-ci-root mount), so this renders just the checkout card.
 *
 * Reuses the orchestration hook + shared Meta event helpers. The payment section
 * is PaymentBlock: Express wallets on top, then a Card / PayPal radio accordion
 * (Stripe PaymentElement for card, our PayPalSection for PayPal) with ONE action
 * button that swaps by selected method — mirroring the GHL accordion.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import {
  funnelEventId,
  fireMetaPixel,
  buildFunnelCustomData,
  contentIdForProduct,
  cartHash,
  type FunnelItem,
} from "@/lib/meta-funnel";
import { PayPalSection } from "@/app/checkout/[slug]/_paypal-section";
import { chargeFxRate } from "@/lib/checkout-cart-money";
import { SubscriptionCheckout } from "@/app/checkout/[slug]/_subscription-checkout";
import { PaymentBlock, type PaymentBlockApi } from "./PaymentBlock";
import { elementsKeyFor, paymentMethodOrderFor } from "./payment-method-order";
import { useCheckoutInline, type IslandConfig } from "./use-checkout-inline";
import { useIslandFunnel, useEmailCaptured } from "./use-island-funnel";
import { COUNTRIES } from "@/lib/countries";
import { shouldTrackAddonAdded } from "@/lib/addon-funnel";

const _stripeByKey = new Map<string, Promise<Stripe | null>>();
function getStripeForKey(pk: string): Promise<Stripe | null> {
  let p = _stripeByKey.get(pk);
  if (!p) {
    p = loadStripe(pk);
    _stripeByKey.set(pk, p);
  }
  return p;
}

const stepBadge = "flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-xs font-semibold text-blue-700";

// Same shape gate as email_captured (page.tsx) and the server's
// sanitizeCustomerEmail: the island submits via button, never a native form
// submit, so type="email" alone surfaces no browser validation bubble.
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Checkout-section copy lives in the island (the "checkout setting"), so the LP
// page needs no checkout markup. fundamentals-in-checkout is the sole native LP
// today; when a second LP adopts the island, lift these to content facts.
const CHECKOUT_HEADING = "Final Step: Complete Your Enrollment";
const CHECKOUT_SUBHEADING =
  "You are just one minute away from instant access to the Bag Designer Academy, including the complete Fundamentals course and your exclusive bonuses.";
const RECAP_GUARANTEE = "30-Day Money-Back Guarantee · Secure SSL Checkout";
const RECAP_TITLE = "Join the Bag Designer Academy";
const RECAP_SUBTITLE = "Master Professional Bag Making Skills & Turn Your Passion Into A Brand.";
const RECAP_RATING = "4.9/5 star reviews";
const RECAP_HIGHLIGHTS = [
  "Everything in Fundamentals (Lifetime Access, Video Lessons)",
  "Pro Addon: Pattern Design Academy",
  "Pro Addon: Bag Business Bootcamp",
  "Masterclass: Rolled Bag Handles (8 Lessons)",
  "Bonus Mini-Course: Sew Your Dust Bag",
  "Exclusive Digital Library (Booklets + Planner)",
  "5-Day Guided Challenge + Patterns",
  "Community & Mobile App Access",
];

export function CheckoutInline({ config }: { config: IslandConfig }) {
  const co = useCheckoutInline(config);

  // First-party funnel events (checkout_started / email_captured here, the
  // payment pair from PaymentBlock). Writes the SAME lp_visitor_sessions row the
  // landing page opened — which is also what makes the buyer abandoned-cart-
  // eligible. rootRef scopes the entry gate to the island (see use-island-funnel).
  const { rootRef, trackFunnel } = useIslandFunnel({
    apiBase: co.apiBase,
    slug: co.config.slug || "",
  });
  useEmailCaptured(trackFunnel, co.email, {
    slug: co.config.slug,
    productId: co.selectedProductId,
    firstName: co.firstName,
    lastName: co.lastName,
  });

  // PaymentBlock renders inside <Elements>, which does NOT reliably commit
  // parent-driven prop updates to its subtree (a parent re-render updates its
  // props but React skips the DOM mutation + passive effects until a state change
  // originates on that fiber). So the add-on total / in-flight flag / element
  // refresh are pushed imperatively: PaymentBlock exposes setters via this ref,
  // and these effects (which DO run — CheckoutInline is outside <Elements>) call
  // them, so the resulting setState originates on PaymentBlock's fiber and paints.
  const paymentApi = useRef<PaymentBlockApi | null>(null);
  // TARGET cents (per-line catalog + FX add-ons) — the pay button must show the
  // same figure the summary lines sum to, not a one-number FX of the USD total.
  useEffect(() => { paymentApi.current?.setTotal(co.chargedDisplayCents); }, [co.chargedDisplayCents]);
  useEffect(() => { paymentApi.current?.setSyncing(co.addonSyncing); }, [co.addonSyncing]);
  useEffect(() => { paymentApi.current?.refresh(); }, [co.paymentRefreshToken]);

  // PayPal requires a typed email (contact identity + abandoned-cart). When the
  // buyer clicks PayPal without one, PayPalSection's createOrder aborts and calls
  // onEmailRequired → show the red border + "Enter an email" and scroll/focus the
  // field (matches scroll+direct). Also set when the buyer blurs the field with
  // an invalid non-empty value (the card path never triggers onEmailRequired).
  // Cleared once a valid email is typed.
  const [emailError, setEmailError] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const requireEmail = useCallback(() => {
    setEmailError(true);
    emailInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => emailInputRef.current?.focus(), 300);
  }, []);

  // ── Meta funnel events (browser pixel + server CAPI). Scroll/mount fires
  // nothing; same event_id formula as /checkout → Meta dedupes. ───────────────
  // Track the product AddToCart last fired for (NOT a boolean): the LP plan
  // button already fires AddToCart and marks this, so the island must not
  // re-fire for the same product — only when the buyer switches to a different
  // one. Prevents the double AddToCart (LP + island radio).
  const atcFiredForId = useRef("");
  const icFired = useRef(false);
  const apiFired = useRef<Set<string>>(new Set());

  const adIds = useCallback(() => {
    const g = window as unknown as { __lpAdIds?: Record<string, string> };
    return {
      ...(g.__lpAdIds || {}),
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    };
  }, []);
  // buildCD/fwd take an optional product override so AddToCart describes the
  // product being added (state may not have updated yet within the same click).
  const buildCD = useCallback((pidOverride?: string) => {
    const pid = pidOverride || co.selectedProductId;
    const tp = co.config.tierProducts?.[pid];
    const base = pidOverride ? (tp?.priceCents || 0) : co.subtotalUsd;
    const items: FunnelItem[] = [
      { id: contentIdForProduct({ sku: tp?.sku, id: pid }), name: tp?.name || "", quantity: 1, priceCents: base },
    ];
    let total = base;
    for (const b of co.bumpOffers) {
      if (b.type === "addon" && co.selectedAddons.has(b.id)) {
        // Add-on price counts toward the cart value, but a bump-offer UUID must
        // NOT enter content_ids — it is not a catalog product (it never becomes
        // an order_item, so Purchase never reports it), and emitting it tanks the
        // catalog match rate. Keep only catalog SKUs in items.
        total += b.addon_price_cents;
      }
    }
    return buildFunnelCustomData(items, pidOverride ? total : co.totalUsd, "USD");
  }, [co.config, co.selectedProductId, co.bumpOffers, co.selectedAddons, co.subtotalUsd, co.totalUsd]);
  const fwd = useCallback(
    (pidOverride?: string) => ({ adIdentifiers: adIds(), slug: co.config.slug, productId: pidOverride || co.selectedProductId, email: co.email.trim() || undefined }),
    [adIds, co.config.slug, co.selectedProductId, co.email],
  );

  const fireAddToCart = useCallback((pid: string) => {
    if (!pid || atcFiredForId.current === pid) return; // one AddToCart per product
    atcFiredForId.current = pid;
    const clickId = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Date.now());
    fireMetaPixel("AddToCart", buildCD(pid), funnelEventId("addtocart", [co.visitorId, pid, clickId]), fwd(pid));
    // First-party too: a visitor who scrolls straight to the island never clicks
    // an LP CTA, so the LP glue's add_to_cart never fires for them. Without this
    // their session records checkout_started + converted but add_to_cart = false,
    // and the funnel report shows purchases exceeding cart-adds. Rides the same
    // per-product guard above, so an LP-driven selection can't double-count.
    trackFunnel("add_to_cart", { product_id: pid, total: co.config.tierProducts?.[pid]?.priceCents || 0 });
  }, [buildCD, fwd, co.visitorId, trackFunnel, co.config.tierProducts]);
  const addonsTracked = useRef<Set<string>>(new Set());
  const toggleAddon = useCallback((id: string) => {
    const track = shouldTrackAddonAdded(co.selectedAddons, addonsTracked.current, id);
    co.toggleAddon(id);
    if (track) trackFunnel("addon_added", { addon_id: id });
  }, [co, trackFunnel]);
  const fireIC = useCallback(() => {
    if (icFired.current) return;
    icFired.current = true;
    fireMetaPixel("InitiateCheckout", buildCD(), funnelEventId("initiatecheckout", [co.visitorId, co.config.slug || "", cartHash([co.selectedProductId])]), fwd());
  }, [buildCD, fwd, co.visitorId, co.config.slug, co.selectedProductId]);
  const fireAddPaymentInfo = useCallback(() => {
    const oid = co.piData?.order_id || co.visitorId;
    if (apiFired.current.has(oid)) return;
    apiFired.current.add(oid);
    const eid = co.piData?.order_id
      ? funnelEventId("addpaymentinfo", [co.piData.order_id])
      : funnelEventId("addpaymentinfo", [co.visitorId, co.config.slug || ""]);
    fireMetaPixel("AddPaymentInfo", buildCD(), eid, fwd());
  }, [buildCD, fwd, co.piData, co.visitorId, co.config.slug]);
  const onFirstInteraction = useCallback(() => {
    fireIC();
    fireAddToCart(co.selectedProductId); // self-guards: no-op if already fired for this product
  }, [fireIC, fireAddToCart, co.selectedProductId]);

  // LP plan buttons set the plan; the LP already fired AddToCart for it, so mark
  // this product as fired so the island doesn't double it.
  //
  // The LP's injected "or pay in monthly installments" control selects the SAME
  // plan and additionally asks for the installment tabs first. It signals that
  // by setting window.__mdbCiInstallments to the product id right before the
  // page's own handleCheckout() runs, so the emitted LP glue (mdbEmbedSelect)
  // stays byte-identical and only ONE add_to_cart fires. selectPlan also takes
  // an explicit option, which is what window.MDBCheckout.selectPlan callers use.
  useEffect(() => {
    const wantsInstallments = (pid: string, opts?: { installments?: boolean }) => {
      if (opts?.installments) return true;
      const flag = window.__mdbCiInstallments;
      return flag === true || flag === pid;
    };
    const selectPlan = (pid: string, opts?: { installments?: boolean }) => {
      const inst = wantsInstallments(pid, opts);
      co.setSelectedProductId(pid);
      atcFiredForId.current = pid;
      // Sticky-off: a later plain plan click must drop back to the default
      // payment-method order, so this is set on EVERY selection, not only when
      // installments were asked for.
      setInstallmentsFirst(inst);
      window.__mdbCiInstallments = undefined;
    };
    // applyPrefill rides the SAME apply-or-buffer pattern: the recovery reader
    // (recovery-deeplink.ts) calls it live when the island is already mounted,
    // and otherwise leaves __mdbCiPendingPrefill for this effect to drain. Both
    // orders therefore work, so it does not matter whether the lazy-mount
    // observer fires before or after the resolve returns.
    window.MDBCheckout = {
      ...(window.MDBCheckout || { mount: () => {} }),
      selectPlan,
      applyPrefill: co.applyPrefill,
    };
    const pending = window.__mdbCiPendingPlan;
    if (pending) {
      // The plan can be buffered as a bare id (mdbEmbedSelect, unchanged) or as
      // { pid, installments } when a caller had the option but no island yet.
      if (typeof pending === "string") selectPlan(pending);
      else selectPlan(pending.pid, { installments: pending.installments });
      window.__mdbCiPendingPlan = undefined;
    }
    const pendingPrefill = window.__mdbCiPendingPrefill;
    if (pendingPrefill) {
      co.applyPrefill(pendingPrefill);
      window.__mdbCiPendingPrefill = undefined;
    }
  }, [co.setSelectedProductId, co.applyPrefill]);

  // Installments-first: the buyer came in through the LP's secondary
  // "pay in monthly installments" control, so Klarna/Affirm lead the Payment
  // Element. Ordering only - eligibility stays Stripe's call, and it is inert
  // while publicConfig.installments_enabled is false.
  const [installmentsFirst, setInstallmentsFirst] = useState(false);

  // Same predicate as /checkout (type free_item + a real threshold) so both
  // surfaces always pick the same gift row.
  const freeGift = co.bumpOffers.find((b) => b.type === "free_item" && (b.threshold_cents || 0) > 0);
  // The gap is a USD difference, not a product price: FX (fmtAddon), never the
  // amount-keyed catalog that `fmt` consults.
  const toGo = freeGift ? (freeGift.threshold_cents || 0) - co.totalUsd : 0;
  const pk = co.publicConfig?.stripe_publishable_key;
  const clientSecret = co.piData?.client_secret;
  // ctMode (ConfirmationToken prototype): the deferred <Elements> mounts on the
  // order's amount (no PI yet) — mode:payment WITHOUT setupFutureUsage so no
  // save-card mandate renders; the PI is minted + vaulted server-side on submit.
  const oneOffAmount = co.piData?.amount_cents;
  const paypalClientId = co.config.paypalClientId || co.publicConfig?.paypal_client_id || "";
  const installmentsOn = !!co.publicConfig?.installments_enabled;
  // What is actually CHARGED, for the provider's installments messaging and for
  // PayPal's Pay Later eligibility. For a South-Africa buyer the ZAR figures on
  // screen are display-only and the settlement is USD, so the messaging must use
  // the USD amount - advertising instalments on money that is never charged
  // would be a second, wrong money surface. Everywhere else the display currency
  // IS the charge currency and totalDisplayCents is the per-line composed total
  // the pay button already shows.
  const chargeCurrency = co.saActive ? "USD" : co.activeCurrency;
  const chargeTotalCents = co.saActive ? co.saBilledUsdCents : co.chargedDisplayCents;
  const addons = co.bumpOffers.filter((b) => b.type === "addon");
  // Recurring plan (e.g. Unlimited Monthly): the SetupIntent/trial subscription
  // flow replaces the one-time PaymentBlock + order summary + bumps.
  const isSubscription = co.selectedPlan?.type === "subscription";
  // Compare-at savings (mirrors the scroll+direct order summary): per-item +
  // per-addon struck price + green "Save $X" and a "You save" total line.
  const planOrig = co.selectedPlan?.original_price_cents || 0;
  const planPrice = co.selectedPlan?.price_cents || 0;
  const planSave = planOrig > planPrice ? planOrig - planPrice : 0;
  const selectedAddonList = co.bumpOffers.filter((b) => b.type === "addon" && co.selectedAddons.has(b.id));
  // Composed from the DISPLAYED per-line savings, exactly like the Order Total
  // right below it. Converting the USD sum as one number yields a third figure
  // that matches neither the lines nor the charge (the Dina incident).
  const totalSavingsDisplayCents =
    co.planSaveCents(co.selectedPlan) +
    selectedAddonList.reduce((s, b) => s + co.addonSaveCents(b.original_price_cents || 0, b.addon_price_cents), 0);
  const recapImage =
    co.config.tierProducts?.[co.config.bestProductId || ""]?.imageUrl ||
    co.config.tierProducts?.[co.selectedProductId]?.imageUrl ||
    "";
  // Per-LP overrides (content facts) fall back to the defaults above.
  const heading = co.config.checkoutHeading || CHECKOUT_HEADING;
  const subheading = co.config.checkoutSubheading ?? CHECKOUT_SUBHEADING;
  const recapTitle = co.config.recapTitle || RECAP_TITLE;
  const recapSubtitle = co.config.recapSubtitle || RECAP_SUBTITLE;
  const recapRating = co.config.recapRating ?? RECAP_RATING;
  const recapGuarantee = co.config.recapGuarantee ?? RECAP_GUARANTEE; // "" (set blank) → hidden
  const proofQuote = co.config.proofQuote?.trim();
  const proofAuthor = co.config.proofAuthor?.trim();
  const guaranteeNote = co.config.guaranteeNote?.trim();
  const highlights = co.config.recapHighlights?.length ? co.config.recapHighlights : RECAP_HIGHLIGHTS;

  // The one-time PaymentBlock is identical in both modes; only the confirm path
  // differs (eager confirmPayment vs ctMode createConfirmationToken). Shared so
  // both <Elements> variants render the same element.
  const oneOffPaymentBlock = (
    <PaymentBlock
      email={co.email}
      firstName={co.firstName}
      lastName={co.lastName}
      billingCountry={co.billingCountry}
      orderId={co.piData?.order_id || ""}
      totalCents={co.chargedDisplayCents}
      fmt={co.fmtTarget}
      onPaymentInitiated={fireAddPaymentInfo}
      trackFunnel={trackFunnel}
      ensureVault={co.ctMode ? undefined : co.vaultCustomer}
      confirmWithToken={co.ctMode ? co.confirmWithToken : undefined}
      syncing={co.addonSyncing}
      amountApiRef={paymentApi}
      installmentsEnabled={installmentsOn}
      installmentsFirst={installmentsFirst}
      chargeCurrency={chargeCurrency}
      chargeTotalCents={chargeTotalCents}
      paypalAvailable={!!paypalClientId}
      renderPayPal={() => (
        <PayPalSection
          productIds={[co.selectedProductId]}
          quantities={[1]}
          lpId={co.config.landingPageId || ""}
          visitorId={co.visitorId}
          email={co.email}
          currency={co.activeCurrency.toLowerCase()}
          slug={co.config.slug || ""}
          selectedAddons={co.selectedAddons}
          bumpOffers={co.bumpOffers}
          amountOverrideCents={co.activeCurrency === "USD" ? 0 : co.totalDisplayCents}
          fxRate={chargeFxRate(co.activeCurrency, co.rates)}
          firstName={co.firstName}
          lastName={co.lastName}
          billingCountry={co.billingCountry}
          clientId={paypalClientId}
          paypalEnv={co.publicConfig?.paypal_env || "live"}
          payLater={installmentsOn}
          onPaymentInitiated={fireAddPaymentInfo}
          onEmailRequired={requireEmail}
          funnelMode="island"
          buttonColor="blue"
          buttonLabel="pay"
        />
      )}
    />
  );

  return (
    // Minimal side gutter on phones (small/old devices need the full width); the
    // card inside is max-width-capped + centered, so desktop is unaffected.
    // data-no-currency: the LP currency-layer scanner must NEVER rewrite island
    // amounts — it substitutes amount-by-amount and cannot keep composite totals
    // consistent (SA: rewritten lines R349+R149 vs an FX'd "R917" total). The
    // island resolves its own display currency incl. ZAR (useCheckoutInline).
    <div ref={rootRef} className="px-1 py-8 text-gray-900 sm:px-3" data-no-currency="1">
      {/* Section heading — owned by the checkout so the LP page stays untouched.
          Copy is per-LP configurable (content facts) with defaults. */}
      <div className="mx-auto mb-8 max-w-2xl text-center">
        <h2 className="text-2xl font-bold sm:text-3xl">{heading}</h2>
        {subheading && <p className="mx-auto mt-2 max-w-xl text-sm text-gray-600 sm:text-base">{subheading}</p>}
      </div>

      <div className="mx-auto flex max-w-[1000px] flex-col gap-6 lg:flex-row lg:items-start">
        {/* Desktop-only left value recap */}
        <aside className="hidden lg:block lg:w-2/5">
          <div className="sticky top-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h3 className="text-xl font-bold">{recapTitle}</h3>
            <p className="mt-1 text-sm text-gray-500">{recapSubtitle}</p>
            {recapRating && (
              <div className="mt-1.5 flex items-center gap-1.5">
                <span className="text-sm leading-none text-yellow-400">★★★★★</span>
                <span className="text-xs font-medium text-gray-500">{recapRating}</span>
              </div>
            )}
            {recapImage && <img src={recapImage} alt={recapTitle} className="mt-4 w-full rounded-xl" />}
            {highlights.length > 0 && (
              <>
                <div className="mb-2 mt-5 text-sm font-bold">What You&apos;ll Get Today:</div>
                <ul className="space-y-2 text-sm text-gray-600">
                  {highlights.map((h, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <svg className="h-4 w-4 flex-shrink-0 text-teal-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
                      {h}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {recapGuarantee && (
              <div className="mt-4 flex items-center gap-2 border-t border-gray-100 pt-4 text-xs text-gray-400">
                <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                {recapGuarantee}
              </div>
            )}
          </div>
        </aside>

        {/* Checkout card — right column on desktop, full width on mobile */}
        <div className="mx-auto w-full max-w-[600px] lg:mx-0 lg:flex-1">
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            {/* Blue header + secure icons */}
        <div className="flex items-center justify-between bg-blue-600 px-5 py-4 text-white">
          <div>
            <div className="text-base font-semibold">Checkout</div>
            <div className="text-xs text-blue-100">100% Secure &amp; Safe Transactions</div>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-500/50">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect width="20" height="14" x="2" y="5" rx="2" /><line x1="2" x2="22" y1="10" y2="10" /></svg>
            </span>
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-500/50">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect width="18" height="11" x="3" y="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
            </span>
          </div>
        </div>

        <div className="space-y-5 p-5">
          {/* Intro guidance — full name + email reveal all payment methods; remind
              the buyer to confirm with their provider (PayPal/Afterpay/bank app). */}
          <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm text-gray-600">Enter your full name and email to see all available payment methods. Some appear afterward.</p>
            <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <span className="text-base leading-none">💡</span>
              <p className="text-sm text-gray-700"><span className="font-semibold">Tip:</span> After choosing your payment method, don&apos;t forget to confirm your purchase with your payment provider (e.g. PayPal, Afterpay, or your bank app) to complete your order successfully.</p>
            </div>
          </div>
          {/* ① Contact */}
          <section>
            <div className="mb-3 flex items-center gap-2"><span className={stepBadge}>1</span><h3 className="text-base font-semibold">Contact Information</h3></div>
            <div className="grid grid-cols-2 gap-2">
              <input value={co.firstName} onChange={(e) => co.setFirstName(e.target.value)} onFocus={onFirstInteraction} name="given-name" autoComplete="given-name" placeholder="First name" className="rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              <input value={co.lastName} onChange={(e) => co.setLastName(e.target.value)} onFocus={onFirstInteraction} name="family-name" autoComplete="family-name" placeholder="Last name" className="rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </div>
            <input ref={emailInputRef} value={co.email} onChange={(e) => { co.setEmail(e.target.value); if (emailError && EMAIL_RE.test(e.target.value.trim())) setEmailError(false); }} onBlur={(e) => { const v = e.target.value.trim(); if (v && !EMAIL_RE.test(v)) setEmailError(true); }} onFocus={onFirstInteraction} type="email" name="email" autoComplete="email" inputMode="email" placeholder="Email address" className={`mt-2 w-full rounded-lg px-3 py-2 text-sm ${emailError ? "border-2 border-red-500" : "border border-gray-300"}`} />
            {emailError && <p className="mt-1 text-xs font-medium text-red-600">Enter an email</p>}
            {/* appearance-none + custom chevron so it renders identically on
                Safari (which otherwise paints a native double-arrow select). */}
            <div className="relative mt-2">
              <select value={co.billingCountry} onChange={(e) => co.setBillingCountry(e.target.value)} name="country" autoComplete="country" className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-9 text-sm text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500">
                <option value="">Country (optional)</option>
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
              <svg className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
            </div>
          </section>

          {/* ② Confirm Your Plan */}
          {co.plans.length > 0 && (
            <section>
              <div className="mb-3 flex items-center gap-2"><span className={stepBadge}>2</span><h3 className="text-base font-semibold">Confirm Your Plan</h3></div>
              {/* Plan rows mirror the scroll+direct PlanSelector: stacked
                  struck-price + bold price + green "Save $X", and a trial
                  subtitle for recurring plans. */}
              <div className="space-y-2" role="radiogroup" aria-label="Plan">
                {co.plans.map((p) => {
                  const sel = p.id === co.selectedProductId;
                  const best = p.id === co.config.bestProductId;
                  const orig = p.original_price_cents || 0;
                  const save = orig > p.price_cents ? orig - p.price_cents : 0;
                  const trialDays = p.trial_period_days || 0;
                  return (
                    <button key={p.id} type="button" role="radio" aria-checked={sel} onClick={() => { co.setSelectedProductId(p.id); fireAddToCart(p.id); }}
                      className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-all ${sel ? "border-teal-500 bg-teal-50 ring-1 ring-teal-500" : "border-gray-200 bg-white hover:border-gray-300"}`}>
                      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${sel ? "border-teal-600" : "border-gray-300"}`}>
                        {sel && <span className="h-2.5 w-2.5 rounded-full bg-teal-600" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-gray-900">{p.name}</span>
                          {best && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">Best Value</span>}
                        </span>
                        {p.type === "subscription" && (
                          <span className="block text-xs text-gray-500">{trialDays > 0 ? `${trialDays}-day free trial — cancel anytime` : "Recurring plan — cancel anytime"}</span>
                        )}
                      </span>
                      <span className="shrink-0 text-right">
                        {save > 0 && <span className="block text-xs text-gray-400 line-through">{co.fmtCompare(orig)}</span>}
                        <span className="text-sm font-bold text-gray-900">{co.fmtPlan(p)}</span>
                        {save > 0 && <span className="block text-[11px] font-medium text-green-600">Save {co.fmtPlanSave(p)}</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {!isSubscription && freeGift && (
            // STABLE DOM: never add/remove nodes when the threshold is crossed.
            // The earlier ternary swapped a Fragment (bar) for a <div> at the
            // crossing — React then hit "removeChild: not a child" mid-reconcile
            // (the Stripe <Elements> subtree re-keys on the same addon change) and
            // BLANKED the whole checkout; it also left the count stale on addon
            // changes. Here only text/class/visibility/width update — no structural
            // churn. Below threshold: generic "Free gift" + bar. At/above: gift name.
            <div className="rounded-xl border border-gray-200 p-3">
              <div className={toGo > 0 ? "text-xs text-gray-600" : "text-xs font-medium text-teal-700"}>
                {toGo > 0
                  ? `🎁 Spend ${co.fmtAddon(toGo)} more for a Free gift`
                  : `🎁 Free gift unlocked: ${freeGift.emoji ? `${freeGift.emoji} ` : ""}${freeGift.gift_name}`}
              </div>
              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded bg-gray-100" style={{ display: toGo > 0 ? "block" : "none" }}>
                <div className="h-full rounded bg-teal-600" style={{ width: `${Math.min(100, freeGift.threshold_cents ? (co.totalUsd / freeGift.threshold_cents) * 100 : 0)}%` }} />
              </div>
            </div>
          )}

          {/* Popular Addons — teal "Add"; when added: mint card + ADDED badge + remove-X */}
          {!isSubscription && addons.length > 0 && (
            <section>
              <h3 className="mb-2 text-sm font-semibold">Popular Addons</h3>
              {co.addonError && (
                <p role="alert" className="mb-2 rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm font-medium text-red-600">
                  {co.addonError}
                </p>
              )}
              <div className="space-y-2">
                {addons.map((b) => {
                  const added = co.selectedAddons.has(b.id);
                  return (
                    <div key={b.id} className={`relative rounded-xl border p-3 ${added ? "border-teal-400 bg-teal-50" : "border-gray-200 bg-white"}`}>
                      {added && (
                        <>
                          <span className="absolute left-3 top-2.5 rounded bg-emerald-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">Added</span>
                          <button type="button" onClick={() => toggleAddon(b.id)} aria-label="Remove add-on" className="absolute right-2.5 top-2 text-gray-400 hover:text-gray-600">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
                          </button>
                        </>
                      )}
                      <div className={`flex items-center gap-3 ${added ? "mt-5" : ""}`}>
                        {b.gift_image_url ? (
                          <img src={b.gift_image_url} alt="" className="h-12 w-12 flex-shrink-0 rounded-md object-cover" />
                        ) : (
                          <span className="h-12 w-12 flex-shrink-0 rounded-md bg-gray-100" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium leading-tight">{b.emoji ? `${b.emoji} ` : ""}{b.gift_name}</div>
                          {b.gift_description ? <div className="truncate text-xs text-gray-500">{b.gift_description}</div> : null}
                          <div className="text-sm">
                            {b.original_price_cents ? <span className="text-gray-400 line-through">{co.fmtCompare(b.original_price_cents)}</span> : null}{" "}
                            <span className="font-semibold text-teal-700">{co.fmtAddon(b.addon_price_cents)}</span>
                          </div>
                        </div>
                        {!added && (
                          <button type="button" onClick={() => toggleAddon(b.id)} className="flex-shrink-0 rounded-md bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-700">
                            Add
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Order Summary (one-time only — the subscription panel shows its own
              "$0 due today / then $X/mo" trial breakdown). Per item + addon shows
              the struck compare-at price + green "Save $X", plus a "You save" total. */}
          {!isSubscription && (
          <section className="space-y-1.5 border-t border-gray-200 pt-4">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <span className="text-sm text-gray-700">{co.selectedPlan?.name}</span>
                {planSave > 0 && <span className="block text-xs font-medium text-green-600">Save {co.fmtPlanSave(co.selectedPlan)}</span>}
              </div>
              <span className="whitespace-nowrap text-right text-sm">
                {planSave > 0 && <span className="block text-xs text-gray-400 line-through">{co.fmtCompare(planOrig)}</span>}
                <span className="text-gray-900">{co.fmtPlan(co.selectedPlan)}</span>
              </span>
            </div>
            {selectedAddonList.map((b) => {
              const save = Math.max(0, (b.original_price_cents || 0) - b.addon_price_cents);
              return (
                <div key={b.id} className="flex items-start justify-between">
                  <div className="min-w-0">
                    <span className="text-sm text-gray-700">{b.gift_name}</span>
                    {save > 0 && <span className="block text-xs font-medium text-green-600">Save {co.fmtAddonSave(b.original_price_cents || 0, b.addon_price_cents)}</span>}
                  </div>
                  <span className="whitespace-nowrap text-right text-sm">
                    {save > 0 && <span className="block text-xs text-gray-400 line-through">{co.fmtCompare(b.original_price_cents)}</span>}
                    <span className="text-gray-900">{co.fmtAddon(b.addon_price_cents)}</span>
                  </span>
                </div>
              );
            })}
            {/* Free gift — a FREE line once the threshold is reached. Always
                rendered (visibility toggled, never added/removed) so crossing the
                threshold can't churn the DOM next to the payment form. */}
            {freeGift && (
              <div className="flex items-start justify-between" style={{ display: toGo <= 0 ? "flex" : "none" }}>
                <span className="text-sm text-gray-700">🎁 {freeGift.emoji ? `${freeGift.emoji} ` : ""}{freeGift.gift_name}</span>
                <span className="whitespace-nowrap text-sm font-semibold text-green-600">FREE</span>
              </div>
            )}
            {/* Per-line composed total — MUST equal the sum of the lines above
                and the pay-button figure (never an FX of the USD sum). */}
            <div className="mt-1 flex justify-between border-t border-gray-100 pt-2 text-base font-semibold"><span>Order Total</span><span>{co.fmtTarget(co.chargedDisplayCents)}</span></div>
            {co.saActive && co.saBilledUsdCents > 0 && (
              <div className="flex justify-between text-xs text-gray-500"><span>Billed in USD</span><span>${(co.saBilledUsdCents % 100 ? (co.saBilledUsdCents / 100).toFixed(2) : String(Math.round(co.saBilledUsdCents / 100)))}</span></div>
            )}
            {totalSavingsDisplayCents > 0 && <div className="flex justify-between text-sm font-semibold text-green-600"><span>You save</span><span>{co.fmtTarget(totalSavingsDisplayCents)}</span></div>}
          </section>
          )}

          {/* Per-LP proof + guarantee condition right above the payment step
              (and so above the buy button). Display-only copy from the LP's
              content facts; absent fields render nothing. */}
          {(proofQuote || guaranteeNote) && (
            <section className="space-y-2">
              {proofQuote && (
                <figure className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                  <div className="text-sm text-amber-400" aria-hidden="true">★★★★★</div>
                  <blockquote className="mt-1 text-sm italic text-gray-700">&ldquo;{proofQuote}&rdquo;</blockquote>
                  {proofAuthor && <figcaption className="mt-1 text-xs font-semibold text-gray-500">{proofAuthor}</figcaption>}
                </figure>
              )}
              {guaranteeNote && (
                <p className="flex items-start gap-2 text-xs text-gray-600">
                  <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                  {guaranteeNote}
                </p>
              )}
            </section>
          )}

          {/* ③ Payment — numbered step heading above the Express row, then the
              Card / PayPal radio accordion (PaymentBlock) or the subscription panel. */}
          <section>
            <div className="mb-3 flex items-center gap-2"><span className={stepBadge}>3</span><h3 className="text-base font-semibold">Payment</h3></div>
            {isSubscription && pk ? (
              <Elements key={`sub-${co.selectedProductId}-${co.activeCurrency}`} stripe={getStripeForKey(pk)} options={{ mode: "setup", currency: co.activeCurrency.toLowerCase(), paymentMethodTypes: ["card"], appearance: { theme: "stripe", variables: { colorPrimary: "#0d9488" } } }}>
                <SubscriptionCheckout
                  productId={co.selectedProductId}
                  currency={co.activeCurrency.toLowerCase()}
                  slug={co.config.slug || ""}
                  lpId={co.config.landingPageId || ""}
                  visitorId={co.visitorId}
                  email={co.email}
                  setEmail={co.setEmail}
                  firstName={co.firstName}
                  setFirstName={co.setFirstName}
                  lastName={co.lastName}
                  setLastName={co.setLastName}
                  billingCountry={co.billingCountry}
                  setBillingCountry={co.setBillingCountry}
                  countries={COUNTRIES}
                  clientId={paypalClientId}
                  paypalEnv={co.publicConfig?.paypal_env || "live"}
                  onPaymentInitiated={fireAddPaymentInfo}
                  hideContact
                  funnelMode="island"
                  onEmailRequired={requireEmail}
                />
              </Elements>
            ) : co.isOneTime && pk && co.ctMode && oneOffAmount ? (
              // ctMode: deferred Elements (mode:payment, NO setupFutureUsage → no mandate).
              <Elements
                key={`ct-${co.selectedProductId}-${co.activeCurrency}-${oneOffAmount}`}
                stripe={getStripeForKey(pk)}
                options={{ mode: "payment", amount: oneOffAmount, currency: co.activeCurrency.toLowerCase(), appearance: { theme: "stripe", variables: { colorPrimary: "#0d9488" } } }}
              >
                {oneOffPaymentBlock}
              </Elements>
            ) : co.isOneTime && pk && clientSecret ? (
              // Keyed on the client secret AND the Payment Element tab order:
              // Stripe fixes that order at element CREATION and ignores it on a
              // later element.update(), so installmentsFirst flipping after the
              // mount (the LP instalments control fires a window event) must
              // remount the tree. The geo billing country is in the key for the
              // same reason: it seeds the element at creation (Klarna
              // eligibility is country-dependent) and it resolves one fetch
              // later. Instalments off → the key is the client secret verbatim
              // (elementsKeyFor).
              <Elements key={elementsKeyFor(clientSecret, paymentMethodOrderFor(installmentsOn, installmentsFirst), co.billingCountry)} stripe={getStripeForKey(pk)} options={{ clientSecret, appearance: { theme: "stripe", variables: { colorPrimary: "#0d9488" } } }}>
                {oneOffPaymentBlock}
              </Elements>
            ) : (
              <div className="rounded-xl border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500">Loading secure payment…</div>
            )}
          </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
